import * as React from "react";
import { FieldValues, UseControllerProps } from "react-hook-form";

import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import { createFilterOptions } from "@mui/material/Autocomplete";
import Checkbox from "@mui/material/Checkbox";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Stack from "@mui/material/Stack";
import type { SxProps } from "@mui/material/styles";

import AutocompleteFormField from "components/autocomplete-form-field";
import { useGetManagedCarePlansQuery } from "services/managed-care-plans";
import { ManagedCarePlan } from "services/types/managed-care-plans.type";

type SelectManagedCarePlanProps = {
  isActive?: boolean;
  onOptionSelected?: (option: { label: string; value: string } | null) => void;
  sx?: SxProps;
  multiple?: boolean;
  enablePortal?: boolean;
};

export type RhfSelectManagedCarePlan<T extends FieldValues> =
  SelectManagedCarePlanProps & UseControllerProps<T>;

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

export default function SelectManagedCarePlan<T extends FieldValues>({
  control,
  name,
  onOptionSelected,
  sx,
  multiple,
  isActive,
  enablePortal
}: RhfSelectManagedCarePlan<T>) {
  const {
    data: managedCarePlans = [],
    isLoading,
    isUninitialized
  } = useGetManagedCarePlansQuery({
    is_active: isActive
  });

  const filterOptions = createFilterOptions({
    matchFrom: "start",
    stringify: (option: { label: string; value: string }) => option.label
  });

  return (
    <AutocompleteFormField
      enablePortal={enablePortal}
      isLoading={isLoading || isUninitialized}
      openOnFocus
      autoHighlight
      autoFocus
      name={name}
      label="By Managed Care Plan"
      filterOptions={filterOptions}
      options={managedCarePlans.map((managedCarePlan: ManagedCarePlan) => ({
        label: managedCarePlan.name,
        value: String(managedCarePlan.id),
        is_active: managedCarePlan.is_active
      }))}
      multiple={multiple}
      getOptionLabel={(option) => {
        return option.label ?? "";
      }}
      renderOption={(props: object, option: any, { selected }: any) => (
        <ListItem {...props}>
          <Stack direction="row" alignItems="center" spacing={1}>
            {multiple && (
              <Checkbox icon={icon} checkedIcon={checkedIcon} checked={selected} />
            )}
            <ListItemText>
              {option.label}
              {option.is_active === false && " (Inactive)"}
            </ListItemText>
          </Stack>
        </ListItem>
      )}
      isOptionEqualToValue={(option, value) => option.value === value.value}
      popupIcon={<ArrowDropDownIcon />}
      control={control}
      onOptionSelected={(option: any) => {
        if (onOptionSelected) {
          onOptionSelected(option);
        }
      }}
      sx={{ ml: 0.5, mt: 0, minWidth: 280, ...sx }}
    />
  );
}
